import { useMemo, useState } from 'react';
import { useAuditEvents, useAuditSummary, type AuditEvent } from '../../api/audit.js';

/**
 * Audit viewer.
 *
 * Append-only, tenant-scoped, auto-refreshing stream of every
 * mutation, query, render, and cube refresh. Filter by action,
 * artifact type, outcome. Click a row to inspect details.
 */
export function AuditAdminPage(): JSX.Element {
  const [actionPrefix, setActionPrefix] = useState('');
  const [artifactType, setArtifactType] = useState('');
  const [outcome, setOutcome] = useState<'' | 'success' | 'failure' | 'denied'>('');
  const [selected, setSelected] = useState<AuditEvent | null>(null);

  const filters = useMemo(
    () => ({
      ...(actionPrefix ? { actionPrefix } : {}),
      ...(artifactType ? { artifactType } : {}),
      ...(outcome ? { outcome } : {}),
      limit: 200,
    }),
    [actionPrefix, artifactType, outcome],
  );

  const events = useAuditEvents(filters);
  const summary = useAuditSummary(24);

  return (
    <>
      <header className="page-title">
        <div>
          <h1>Audit</h1>
          <p className="page-title-subtitle">
            Append-only, tenant-scoped log of every mutation, query, render, and cube refresh.
            Auto-refreshes every 8 seconds. Foundation for SOC 2 evidence exports.
          </p>
        </div>
        <div className="page-title-actions">
          <span className="badge badge--success">
            <span className="badge-dot" /> Live
          </span>
        </div>
      </header>

      <div className="stack-lg">
        {/* Summary strip */}
        <div className="card">
          <div className="card-header">
            <h3>Activity · last 24 hours</h3>
            <span className="text-mute text-sm">
              {summary.data?.actions.reduce((n, a) => n + a.count, 0) ?? 0} events
            </span>
          </div>
          <div className="stat-strip" style={{ borderBottom: 0 }}>
            {summary.isLoading && <span className="text-dim">Loading…</span>}
            {summary.data && summary.data.actions.length === 0 && (
              <span className="text-mute text-sm">No events yet.</span>
            )}
            {summary.data?.actions.slice(0, 6).map((a) => (
              <div className="stat" key={a.action}>
                <div className="stat-label">{a.action.replace(/\./g, ' · ')}</div>
                <div className="stat-value">{a.count.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Filter bar */}
        <div className="card">
          <div className="card-body">
            <div
              className="row"
              style={{ gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'flex-end' }}
            >
              <label className="field" style={{ margin: 0, flex: 1, minWidth: 180 }}>
                <span className="field-label">Action prefix</span>
                <input
                  className="input input--sm input--mono"
                  value={actionPrefix}
                  onChange={(e) => setActionPrefix(e.target.value)}
                  placeholder="e.g. report. or query."
                />
              </label>
              <label className="field" style={{ margin: 0, flex: 1, minWidth: 160 }}>
                <span className="field-label">Artifact type</span>
                <select
                  className="select input--sm"
                  value={artifactType}
                  onChange={(e) => setArtifactType(e.target.value)}
                >
                  <option value="">Any</option>
                  <option value="report_template">report_template</option>
                  <option value="dataset">dataset</option>
                  <option value="data_source">data_source</option>
                  <option value="materialization">materialization</option>
                </select>
              </label>
              <label className="field" style={{ margin: 0, flex: 1, minWidth: 140 }}>
                <span className="field-label">Outcome</span>
                <select
                  className="select input--sm"
                  value={outcome}
                  onChange={(e) => setOutcome(e.target.value as typeof outcome)}
                >
                  <option value="">Any</option>
                  <option value="success">success</option>
                  <option value="failure">failure</option>
                  <option value="denied">denied</option>
                </select>
              </label>
              {(actionPrefix || artifactType || outcome) && (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => {
                    setActionPrefix('');
                    setArtifactType('');
                    setOutcome('');
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Event list */}
        <div className="card">
          <div className="card-header">
            <h3>Events</h3>
            <span className="text-mute text-sm">
              {events.data ? `${events.data.length} row${events.data.length === 1 ? '' : 's'}` : ''}
            </span>
          </div>
          {events.isLoading && <div className="card-body text-dim">Loading…</div>}
          {events.data && events.data.length === 0 && (
            <div className="empty-state" style={{ border: 'none' }}>
              <div className="empty-state-icon">∅</div>
              <h3>No events match</h3>
              <p>Adjust the filters, or do something in the app — every action is logged here.</p>
            </div>
          )}
          {events.data && events.data.length > 0 && (
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 170 }}>When</th>
                  <th>Action</th>
                  <th>Artifact</th>
                  <th>Outcome</th>
                  <th>User</th>
                  <th style={{ width: 140 }}>Correlation</th>
                </tr>
              </thead>
              <tbody>
                {events.data.map((e) => (
                  <tr key={e.id} onClick={() => setSelected(e)} style={{ cursor: 'pointer' }}>
                    <td className="text-dim text-sm">{formatTime(e.occurredAt)}</td>
                    <td>
                      <code>{e.action}</code>
                    </td>
                    <td className="text-sm">
                      {e.artifactType ? (
                        <span>
                          <span className="text-mute">{e.artifactType}</span>
                          {e.artifactId && (
                            <>
                              {' · '}
                              <span className="cell-mono">{e.artifactId.slice(0, 8)}…</span>
                            </>
                          )}
                        </span>
                      ) : (
                        <span className="text-mute">—</span>
                      )}
                    </td>
                    <td>
                      <OutcomeBadge outcome={e.outcome} />
                    </td>
                    <td className="cell-mono">{e.userId ? e.userId.slice(0, 8) : '—'}</td>
                    <td className="cell-mono">{e.correlationId.slice(0, 8)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selected && <EventDetailModal event={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

function OutcomeBadge({ outcome }: { outcome: string }): JSX.Element {
  const cls =
    outcome === 'success'
      ? 'badge--success'
      : outcome === 'denied'
        ? 'badge--warning'
        : outcome === 'failure'
          ? 'badge--danger'
          : 'badge--neutral';
  return <span className={`badge ${cls}`}>{outcome}</span>;
}

function EventDetailModal({ event, onClose }: { event: AuditEvent; onClose(): void }): JSX.Element {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 680 }}>
        <header className="modal-header">
          <div>
            <h2>
              <code>{event.action}</code>
            </h2>
            <div className="modal-subtitle">
              {formatTime(event.occurredAt)} · <OutcomeBadge outcome={event.outcome} />
            </div>
          </div>
          <button className="btn-icon" type="button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <div className="modal-body stack">
          <FieldRow label="Correlation" value={event.correlationId} mono />
          {event.userId && <FieldRow label="User" value={event.userId} mono />}
          {event.artifactType && (
            <FieldRow
              label="Artifact"
              value={`${event.artifactType}${event.artifactId ? ' · ' + event.artifactId : ''}`}
              mono
            />
          )}
          <div>
            <div className="field-label" style={{ marginBottom: 6 }}>
              Details
            </div>
            <pre
              style={{
                background: 'var(--color-surface-3)',
                padding: 'var(--space-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border)',
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--font-size-sm)',
                overflow: 'auto',
                margin: 0,
                maxHeight: 300,
              }}
            >
              {JSON.stringify(event.details, null, 2)}
            </pre>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="row" style={{ gap: 'var(--space-4)' }}>
      <div className="field-label" style={{ width: 120, marginBottom: 0 }}>
        {label}
      </div>
      <div className={mono ? 'text-mono' : 'text-sm'} style={{ wordBreak: 'break-all' }}>
        {value}
      </div>
    </div>
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const ageSecs = Math.round((now - d.getTime()) / 1000);
  if (ageSecs < 60) return `${ageSecs}s ago`;
  if (ageSecs < 3600) return `${Math.round(ageSecs / 60)}m ago`;
  if (ageSecs < 86400) return d.toLocaleTimeString();
  return d.toLocaleString();
}
